import { FC, useCallback, useState } from "react"
import Cropper, { Area, Point } from "react-easy-crop"
import { X as CloseIcon, ZoomIn, ZoomOut } from "lucide-react"
import BBModal from "../modal"
import { DefaultBtn, PrimaryBtn } from "../buttons"
import { getCroppedImg } from "./crop-image"

interface Props {
  show: boolean
  imageSrc: string
  fileName: string
  cropShape?: "round" | "rect"
  aspect?: number
  outputSize?: number
  onCancel: () => void
  onCropped: (file: File) => void
}

const MIN_ZOOM = 1
const MAX_ZOOM = 3
const ZOOM_STEP = 0.2

const clampZoom = (value: number) =>
  Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number(value.toFixed(2))))

const CropModal: FC<Props> = ({
  show,
  imageSrc,
  fileName,
  cropShape = "round",
  aspect = 1,
  outputSize = 512,
  onCancel,
  onCropped
}) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [processing, setProcessing] = useState(false)

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return
    try {
      setProcessing(true)
      const file = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        outputSize,
        fileName
      )
      onCropped(file)
    } finally {
      setProcessing(false)
    }
  }

  // Branded fill on the zoom track: purple up to the thumb, neutral after.
  const zoomPct = ((zoom - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100

  return (
    <BBModal
      show={show}
      size="lg"
      noPadding
      showCloseButton={false}
      onClose={onCancel}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-neutral-200">
        <div>
          <h3 className="text-base font-semibold text-base-dark">
            Position your photo
          </h3>
          <p className="text-xs text-neutral-400 mt-0.5">
            Drag to reposition · scroll or pinch to zoom
          </p>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onCancel}
          disabled={processing}
          className="-mr-1.5 -mt-0.5 rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-40"
        >
          <CloseIcon size={20} aria-hidden="true" />
        </button>
      </div>

      {/* Crop stage */}
      <div className="relative h-[360px] w-full bg-[radial-gradient(circle_at_center,#26203b_0%,#14121d_100%)]">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          aspect={aspect}
          cropShape={cropShape}
          showGrid={false}
          restrictPosition
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          style={{
            cropAreaStyle: {
              border: "2px solid rgba(255,255,255,0.92)",
              boxShadow: "0 0 0 9999px rgba(10, 8, 20, 0.62)",
              color: "rgba(10, 8, 20, 0.62)"
            }
          }}
        />
      </div>

      {/* Controls */}
      <div className="px-6 py-5">
        <div className="flex items-center gap-4">
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => setZoom(z => clampZoom(z - ZOOM_STEP))}
            disabled={zoom <= MIN_ZOOM}
            className="text-neutral-400 transition-colors hover:text-neutral-700 disabled:opacity-30 disabled:hover:text-neutral-400"
          >
            <ZoomOut size={18} />
          </button>

          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            aria-label="Zoom"
            onChange={e => setZoom(Number(e.target.value))}
            className="bb-crop-zoom h-1 flex-1 cursor-pointer appearance-none rounded-full"
            style={{
              background: `linear-gradient(to right, #5016a3 ${zoomPct}%, #e5e5e5 ${zoomPct}%)`
            }}
          />

          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => setZoom(z => clampZoom(z + ZOOM_STEP))}
            disabled={zoom >= MAX_ZOOM}
            className="text-neutral-400 transition-colors hover:text-neutral-700 disabled:opacity-30 disabled:hover:text-neutral-400"
          >
            <ZoomIn size={18} />
          </button>
        </div>

        <div className="flex justify-end gap-3 pt-6">
          <DefaultBtn onClick={onCancel} disabled={processing}>
            Cancel
          </DefaultBtn>
          <PrimaryBtn
            onClick={handleConfirm}
            disabled={!croppedAreaPixels}
            isLoading={processing}
          >
            {processing ? "Saving…" : "Save photo"}
          </PrimaryBtn>
        </div>
      </div>

      {/* Branded range thumb (Tailwind can't target ::*-thumb pseudo-elements) */}
      <style>{`
        .bb-crop-zoom::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #5016a3;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
          transition: transform 0.12s ease;
        }
        .bb-crop-zoom::-webkit-slider-thumb:hover { transform: scale(1.15); }
        .bb-crop-zoom::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #5016a3;
          border: 2px solid #fff;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
        }
        .bb-crop-zoom:focus { outline: none; }
        .bb-crop-zoom:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 4px rgba(112, 66, 190, 0.3);
        }
      `}</style>
    </BBModal>
  )
}

export default CropModal
